import React, { useEffect, useState } from 'react';
import Modal from '../../../components/Modal';
import TextField from '../../../components/TextField';
import Button from '../../../components/Button';
import { FiMapPin } from 'react-icons/fi';
import Icon from '../../../components/Icon';
import PakistanPlaceSearch from '../../../components/PakistanPlaceSearch';
import BranchMapPickerModal from '../../../components/BranchMapPickerModal';
import type { RegionLocation } from '../service/locationsService';

type Mode = 'add' | 'edit' | 'view';

type Props = {
    isOpen: boolean;
    onClose: () => void;
    mode: Mode;
    initial: RegionLocation | null;
    onSave: (payload: { name: string; headOfficeAddress: string; latitude: string; longitude: string }) => Promise<void>;
};

function openMaps(latStr: string, lonStr: string) {
    const lat = parseFloat(String(latStr).replace(/,/g, '').trim());
    const lng = parseFloat(String(lonStr).replace(/,/g, '').trim());
    if (Number.isNaN(lat) || Number.isNaN(lng)) return;
    window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank', 'noopener,noreferrer');
}

function FormCell({ children, className = '' }: { children?: React.ReactNode; className?: string }) {
    return <div className={`min-w-0 ${className}`}>{children}</div>;
}

export default function LocationFormModal({ isOpen, onClose, mode, initial, onSave }: Props) {
    const gmapsKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    const [mapPickerOpen, setMapPickerOpen] = useState(false);
    const [name, setName] = useState('');
    const [headOfficeAddress, setHeadOfficeAddress] = useState('');
    const [latitude, setLatitude] = useState('');
    const [longitude, setLongitude] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const readOnly = mode === 'view';

    useEffect(() => {
        if (!isOpen) return;
        setError('');
        if (initial) {
            setName(initial.name || '');
            setHeadOfficeAddress(initial.headOfficeAddress || '');
            const loc = initial.location;
            setLatitude(loc?.lat != null ? String(loc.lat) : '');
            setLongitude(loc?.long != null ? String(loc.long) : '');
        } else {
            setName('');
            setHeadOfficeAddress('');
            setLatitude('');
            setLongitude('');
        }
    }, [isOpen, initial, mode]);

    const title =
        mode === 'view' ? 'Location details' : mode === 'edit' ? 'Edit location' : 'Add location';

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (readOnly) return;
        const n = name.trim();
        if (!n) {
            setError('Region name is required');
            return;
        }
        setLoading(true);
        setError('');
        try {
            await onSave({ name: n, headOfficeAddress: headOfficeAddress.trim(), latitude, longitude });
            onClose();
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to save location');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Modal isOpen={isOpen} onClose={onClose} title={title} size="lg">
                <form onSubmit={handleSubmit} className="flex flex-col space-y-4 p-4 sm:p-6">
                    <TextField
                        label="Region name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="e.g. Karachi Central"
                        required={!readOnly}
                        disabled={readOnly}
                    />
                    <TextField
                        label="Head office address"
                        value={headOfficeAddress}
                        onChange={(e) => setHeadOfficeAddress(e.target.value)}
                        placeholder="Full address"
                        disabled={readOnly}
                    />

                    {!readOnly && (
                        <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-6 lg:items-end">
                            <FormCell className="min-w-0 sm:col-span-2 lg:col-span-2">
                                <PakistanPlaceSearch
                                    apiKey={gmapsKey}
                                    onResolved={(p) => {
                                        setLatitude(String(p.lat));
                                        setLongitude(String(p.lng));
                                    }}
                                />
                            </FormCell>
                            <FormCell>
                                <label className="form-field-label">Long</label>
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    value={longitude}
                                    onChange={(e) => setLongitude(e.target.value)}
                                    placeholder="69.07"
                                    className="form-control text-sm"
                                />
                            </FormCell>
                            <FormCell>
                                <label className="form-field-label">Lat</label>
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    value={latitude}
                                    onChange={(e) => setLatitude(e.target.value)}
                                    placeholder="24.01"
                                    className="form-control text-sm"
                                />
                            </FormCell>
                            <FormCell className="flex flex-col justify-end lg:col-span-2 lg:w-auto">
                                <span className="form-field-label opacity-0 select-none">Map</span>
                                <button
                                    type="button"
                                    title={gmapsKey?.trim() ? 'Pick on map (Karachi default)' : 'Open in Google Maps'}
                                    onClick={() => {
                                        if (gmapsKey?.trim()) setMapPickerOpen(true);
                                        else openMaps(latitude, longitude);
                                    }}
                                    className="inline-flex h-9 w-full max-w-[2.25rem] items-center justify-center rounded-lg border border-gray-300 bg-gray-100 text-gray-700 shadow-sm transition hover:bg-gray-50 dark:border-gray-600 dark:bg-black/30 dark:text-gray-200 dark:hover:bg-black/50"
                                >
                                    <Icon icon={FiMapPin} className="h-4 w-4" />
                                </button>
                            </FormCell>
                        </div>
                    )}

                    {readOnly && (
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <FormCell>
                                <label className="form-field-label">Latitude</label>
                                <p className="mt-1 text-sm text-gray-800 dark:text-gray-200">
                                    {latitude || '—'}
                                </p>
                            </FormCell>
                            <FormCell>
                                <label className="form-field-label">Longitude</label>
                                <p className="mt-1 text-sm text-gray-800 dark:text-gray-200">
                                    {longitude || '—'}
                                </p>
                            </FormCell>
                        </div>
                    )}

                    {error && (
                        <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600 dark:bg-red-500/10 dark:text-red-400">
                            {error}
                        </div>
                    )}

                    <div className="flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
                        <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
                            {readOnly ? 'Close' : 'Cancel'}
                        </Button>
                        {!readOnly && (
                            <Button type="submit" disabled={loading}>
                                {loading ? 'Saving…' : mode === 'edit' ? 'Save changes' : 'Add location'}
                            </Button>
                        )}
                    </div>
                </form>
            </Modal>
            {!readOnly && (
                <BranchMapPickerModal
                    isOpen={mapPickerOpen}
                    onClose={() => setMapPickerOpen(false)}
                    apiKey={gmapsKey}
                    latitude={latitude}
                    longitude={longitude}
                    onSubmit={(lat, lng) => {
                        setLatitude(String(lat));
                        setLongitude(String(lng));
                    }}
                />
            )}
        </>
    );
}
